import type { ContenidoDeLaWeb, PaginaEnBorrador } from '../api/consultas'

/**
 * La primera publicación de una web, vista desde el panel (CLAUDE.md §13): **el contenido
 * primero y el diseño después**. La previa enseña lo publicado, publicar el diseño no
 * publica páginas, y el alta deja todas las páginas en borrador. Sin avisar, una web recién
 * creada sale en «Esta página no existe»: en la previa, y en internet al publicarla.
 *
 * Publicar lo hace «Publicar mi web» (`publicar/`), el mismo diálogo en la cabecera, en
 * Diseño y al final del alta. Aquí quedan las frases del aviso de la previa vacía.
 */

type Contenido = ContenidoDeLaWeb | null | undefined

/** Sin la portada publicada, la previa y la web abren en «Esta página no existe». Sin datos (un núcleo viejo, o sin permiso de diseño) no se avisa de nada. */
export const faltaLaPortada = (contenido: Contenido): boolean => Boolean(contenido) && !contenido!.inicioPublicado

/** La portada: la página «inicio» sin madre (la marca el núcleo; un núcleo de antes solo manda el slug). */
export const esPortada = (b: PaginaEnBorrador): boolean => b.portada ?? b.slug === 'inicio'

/**
 * Si la portada está entre los borradores: publicarlos la arregla. Si no está, la web no
 * tiene portada que publicar, y hay que crearla.
 */
export const portadaEnBorrador = (contenido: ContenidoDeLaWeb): boolean => contenido.borradores.some(esPortada)

/** «tu página está en borrador» / «tus 6 páginas están en borrador». */
export const fraseDePaginas = (n: number): string => (n <= 0 ? 'no te queda ninguna página en borrador' : n === 1 ? 'tu página está en borrador' : `tus ${n} páginas están en borrador`)

/** Cuando no hay portada que publicar: se dice qué hacer, en vez de un botón que no lleva a nada. */
export const SIN_PAGINA_DE_INICIO = 'Tu web no tiene página de inicio. Créala en Páginas con la dirección «inicio», sin página padre: es la que se abre en la portada.'

type EstadoDeNavegacion = null | undefined | { aviso?: null | string; avisos?: null | unknown }

/**
 * Los avisos que trae la navegación desde el asistente, uno por línea. Ahora llegan en
 * `avisos: string[]`; antes, juntos en un solo `aviso`, y un enlace guardado o una pestaña
 * abierta pueden traer todavía la forma vieja.
 */
export const avisosDe = (estado: EstadoDeNavegacion): string[] => {
  const lista = Array.isArray(estado?.avisos) ? estado.avisos.filter((a): a is string => typeof a === 'string') : []
  const viejo = typeof estado?.aviso === 'string' ? [estado.aviso] : []
  return [...lista, ...viejo].map((a) => a.trim()).filter((a, i, todos) => a !== '' && todos.indexOf(a) === i)
}
